import React, { useState, useRef, useCallback } from 'react';
import { CONCEPT_CATALOG, suggestFromVideoAnalysis } from './conceptCatalog';
import { UploadIcon } from '../icons/UploadIcon';
import { DeleteIcon } from '../icons/DeleteIcon';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { generateConcepts as generateConceptsApi, fetchUrlReference, fetchSocialAccount, detectSocialPlatform, getVideoUploadUrl, uploadVideoToGcs, analyzeVideo } from '../../services/aiService';
import { getModelForCapability } from '../../utils/modelHelper';

/**
 * ConceptGenerator — "Generate concepts for video" mode.
 *
 * Flow:
 *   1. Brand/Product/Business name (free-form input).
 *   2. Reference (website/social URL, reference video URL, or uploaded video).
 *   3. Industry picker (hover tooltip with desc + benefit).
 *   4. Video type picker (industry-specific, hover tooltip).
 *   5. "Generate Concepts" → 4 distinct concept cards are composed client-side
 *      using angle-based templates. No LLM call at this step — the LLM is
 *      used AFTER the user selects a concept, when the existing
 *      "Generate Script" flow runs.
 *   6. Regenerate re-rolls the wording variants so re-clicks give fresh copy.
 *   7. Selecting a concept hands a detailed brief to the parent via
 *      onConceptReady(text, { brandName }), which pre-fills the existing
 *      "Start with Concept" textarea and flips to that mode.
 *
 * Why templates rather than an LLM call at this step?
 *   The admin backend's /ai/generate-script-async endpoint is hardwired to
 *   produce a full narrative screenplay; it does not honour user instructions
 *   asking for JSON / multi-concept output. Until a dedicated
 *   /ai/generate-concepts endpoint exists, deterministic templates give us
 *   4 useful starting points with zero latency and zero failure modes. The
 *   creative heavy-lift still happens when the user clicks "Generate Script"
 *   in the next step.
 */

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Format big numbers as 1.2K / 3.4M for compact engagement display.
const formatCount = (n) => {
    if (n == null) return '';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
    return String(n);
};

const buildConcepts = ({ brandName, industry, videoType, duration, castMode = 'with_actors' }) => {
    const brand = brandName.trim();
    const industryName = industry.name;
    const videoTypeName = videoType.name;
    const benefit = videoType.benefit;
    const industryDriver = industry.benefit;
    const durationLine = duration ? ` Target duration: ${duration}.` : '';
    const productOnly = castMode === 'product_only';
    const castNote = productOnly
        ? ' This is a PRODUCT-ONLY video — zero people on screen; the product is the hero of every shot.'
        : '';

    // Angle 1 — Emotional Story
    const emotionalTitles = productOnly
        ? [
            `The quiet story of ${brand}`,
            `Anatomy of ${brand}`,
            `${brand}, in close-up`,
        ]
        : [
            `Why people choose ${brand}`,
            `A day with ${brand}`,
            `${brand}: the moment it clicked`,
        ];
    const emotionalHooks = productOnly
        ? [
            `Open on a slow, intimate product macro — every texture and shadow of ${brand} revealed.`,
            `Pure ambient open: ${brand} alone on set, dressed in light, waiting.`,
            `Tactile detail first — a ${brand} surface catches a single moving light source.`,
        ]
        : [
            `Open on a relatable frustration — then show ${brand} solve it in seconds.`,
            `Lead with a candid moment from a real ${industryName.toLowerCase()} customer that hooks emotion.`,
            `Start on a quiet, cinematic detail that makes the viewer lean in.`,
        ];

    // Angle 2 — Bold Hook / Trend
    const boldTitles = [
        `The ${videoTypeName} that stops the scroll for ${brand}`,
        `${brand} vs. expectations`,
        `3 seconds to ${brand}`,
    ];
    const boldHooks = productOnly
        ? [
            `Pattern interrupt in frame 1 — a bold product visual of ${brand} that viewers don't expect.`,
            `A surprising on-screen claim: "${brand} in 3 seconds?" then deliver — all via product + typography.`,
            `Cold open with a viral product shot that breaks the ${industryName.toLowerCase()} cliché.`,
        ]
        : [
            `Pattern interrupt in frame 1 — a bold visual or punchy stat viewers don't expect.`,
            `A surprising claim on screen in second 1: "${brand} in 3 seconds?" then deliver.`,
            `Cold open with a viral-style hook that breaks a ${industryName.toLowerCase()} cliché.`,
        ];

    // Angle 3 — Problem → Solution
    const psTitles = [
        `${brand}: the fix for ${industryName.toLowerCase()}'s biggest headache`,
        `Before ${brand}, after ${brand}`,
        `Stop doing this. Start using ${brand}.`,
    ];
    const psHooks = productOnly
        ? [
            `Frame 1: a symbol of the pain point — then cut to ${brand} replacing it, no people in sight.`,
            `Split screen: old-way product clutter vs. a single clean shot of ${brand}.`,
            `Cold open on a cluttered surface; one by one everything is replaced by ${brand}.`,
        ]
        : [
            `Frame 1 nails the pain point in a single visual — then ${brand} walks in as the answer.`,
            `Split screen: the old way vs. ${brand}. Viewers decide in 2 seconds.`,
            `Cold open on a failure state everyone in ${industryName.toLowerCase()} recognises.`,
        ];

    // Angle 4 — Proof / Results / Social Proof (product-only version replaces testimonials with data)
    const proofTitles = productOnly
        ? [
            `${brand} by the numbers`,
            `What's inside ${brand}`,
            `${brand}: the proof on the label`,
        ]
        : [
            `Real results from ${brand}`,
            `What ${brand} customers don't tell you`,
            `${brand} by the numbers`,
        ];
    const proofHooks = productOnly
        ? [
            `Open on ${brand}'s packaging — data points animate in over the real product.`,
            `A hard number on screen: "${brand} in week 1" — then show the proof on the label, not on a face.`,
            `Lead with a typographic stat stacked over a slow rotating ${brand} hero shot.`,
        ]
        : [
            `Open on a real result — before/after, data point, or testimonial quote.`,
            `Lead with a customer's raw reaction the first time they tried ${brand}.`,
            `A hard number on screen: "${brand} in week 1" — then show the proof.`,
        ];

    // Descriptions — tailor narrative device to cast mode
    const describe = (angle) => {
        if (productOnly) {
            switch (angle) {
                case 'emotional':
                    return `A product-first ${videoTypeName.toLowerCase()} that treats ${brand} like the protagonist. No actors, no narrators — just the object, its texture, its light, its detail. The edit privileges intimacy and craft, ending on a single beauty shot that locks ${brand} into memory for a ${industryName.toLowerCase()} audience.${durationLine}${castNote}`;
                case 'bold':
                    return `A scroll-stopping product-only ${videoTypeName.toLowerCase()} built around a sharp visual hook on ${brand} in the first 2 seconds. Fast cuts, confident on-screen typography, and one bold claim — zero people — that the viewer has to verify by staying till the end. Delivers on "${benefit.toLowerCase()}".${durationLine}${castNote}`;
                case 'ps':
                    return `A classic problem → solution story told entirely through product and environment — no characters. Act 1: the pain is visualised through objects (${industryDriver.toLowerCase()}). Act 2: ${brand} replaces them. Act 3: the clean result. Closes on a single decisive CTA that earns "${benefit.toLowerCase()}".${durationLine}${castNote}`;
                case 'proof':
                    return `A proof-first product-only ${videoTypeName.toLowerCase()} that stacks credibility cues — real numbers, lab shots, ingredient / feature close-ups on ${brand}. No testimonial faces; the claims live on the packaging, the product, and the typography. This concept trades social proof for material proof — and material proof is what turns ${industryName.toLowerCase()} viewers into buyers.${durationLine}${castNote}`;
                default: return '';
            }
        }
        switch (angle) {
            case 'emotional':
                return `An emotionally-anchored ${videoTypeName.toLowerCase()} that follows one relatable ${industryName.toLowerCase()} moment from start to finish. ${brand} appears organically as the turning point rather than a hard pitch. The closing beat leaves the viewer with a feeling, a face, and a reason to remember the brand — exactly the "${benefit.toLowerCase()}" the ${videoTypeName.toLowerCase()} format needs.${durationLine}`;
            case 'bold':
                return `A scroll-stopping ${videoTypeName.toLowerCase()} built around a sharp, punchy hook in the first 2 seconds. Fast cuts, confident voiceover, and one bold claim the viewer has to verify by staying till the end. ${brand} is the protagonist — not the caption — and the payoff delivers on "${benefit.toLowerCase()}".${durationLine}`;
            case 'ps':
                return `A classic problem → solution ${videoTypeName.toLowerCase()} tailored for ${industryName.toLowerCase()} (${industryDriver.toLowerCase()}). Act 1: land the pain. Act 2: ${brand} walks in. Act 3: the clean result. Every frame justifies why ${brand} is the easier choice — closing on a single, decisive CTA that earns "${benefit.toLowerCase()}".${durationLine}`;
            case 'proof':
                return `A proof-first ${videoTypeName.toLowerCase()} that stacks credibility cues — real numbers, real faces, real outcomes from ${brand} customers. The edit privileges authenticity over polish, so nothing feels staged. This concept trades flash for trust, and trust is what turns ${industryName.toLowerCase()} viewers into buyers — fulfilling the "${benefit.toLowerCase()}" promise.${durationLine}`;
            default: return '';
        }
    };

    return [
        { title: pickRandom(emotionalTitles), hook: pickRandom(emotionalHooks), description: describe('emotional') },
        { title: pickRandom(boldTitles),      hook: pickRandom(boldHooks),      description: describe('bold') },
        { title: pickRandom(psTitles),        hook: pickRandom(psHooks),        description: describe('ps') },
        { title: pickRandom(proofTitles),     hook: pickRandom(proofHooks),     description: describe('proof') },
    ];
};

// Hover-tooltip card used by industry and video-type pickers.
const TooltipCard = ({ title, subtitle, benefit, selected, onClick, testid }) => (
    <button
        type="button"
        onClick={onClick}
        className={`group relative text-left p-3 rounded-xl border transition-all ${
            selected
                ? 'bg-violet-500/15 border-violet-500/60 ring-1 ring-violet-500/30'
                : 'bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/10'
        }`}
        data-testid={testid}
    >
        <div className="font-semibold text-sm text-white leading-tight">{title}</div>
        {subtitle && (
            <div className="text-[11px] text-zinc-400 mt-0.5 leading-snug line-clamp-2">{subtitle}</div>
        )}
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-20">
            <div className="bg-zinc-900 border border-white/15 rounded-lg p-3 shadow-xl shadow-black/60">
                <div className="text-xs font-semibold text-white mb-1">{title}</div>
                {subtitle && <div className="text-[11px] text-zinc-300 leading-relaxed">{subtitle}</div>}
                {benefit && (
                    <div className="text-[11px] text-violet-300 mt-2 flex items-start gap-1">
                        <span>👉</span>
                        <span>{benefit}</span>
                    </div>
                )}
            </div>
        </div>
    </button>
);

// Phase 2 — render a Gemini video-analysis result with title, summary,
// highlights, scene breakdown disclosure, and transcript disclosure.
const VideoAnalysisCard = ({ analysis, onClear, onAttach, formatDuration }) => {
    const [showSegments, setShowSegments] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);
    const vs = analysis.visualStyle || {};
    const aud = analysis.audioStyle || {};
    const styleChips = [
        vs.mood && { label: 'mood', value: vs.mood },
        vs.pacing && { label: 'pacing', value: vs.pacing },
        aud.music_genre && { label: 'music', value: aud.music_genre },
        aud.voice_tone && { label: 'voice', value: aud.voice_tone },
    ].filter(Boolean);
    const palette = (vs.color_palette || []).slice(0, 5);

    return (
        <div className="mt-3 p-4 bg-white/5 border border-violet-500/30 rounded-xl space-y-3" data-testid="video-analysis-card">
            <div className="flex items-start gap-3">
                {analysis.thumbnail ? (
                    <img
                        src={analysis.thumbnail}
                        alt=""
                        className="w-24 aspect-video object-cover rounded-lg flex-shrink-0 border border-white/10"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                ) : (
                    <div className="w-24 aspect-video rounded-lg bg-violet-500/20 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">
                        {analysis.siteName || analysis.sourceType || 'video'}
                        {analysis.durationSeconds ? ` · ${formatDuration(analysis.durationSeconds)}` : ''}
                    </div>
                    <div className="text-sm font-semibold text-white leading-tight">{analysis.title || 'Untitled video'}</div>
                    {analysis.text && (
                        <div className="text-xs text-zinc-400 mt-1 line-clamp-3">{analysis.text}</div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 flex-shrink-0"
                    data-testid="video-analysis-clear-btn"
                    title="Discard analysis"
                >
                    <DeleteIcon className="w-4 h-4" />
                </button>
            </div>

            {analysis.highlights?.length > 0 && (
                <ul className="space-y-1 pl-1" data-testid="video-analysis-highlights">
                    {analysis.highlights.slice(0, 4).map((h, i) => (
                        <li key={i} className="text-xs text-zinc-300 leading-relaxed flex gap-2">
                            <span className="text-violet-400 flex-shrink-0">•</span>
                            <span>{h}</span>
                        </li>
                    ))}
                </ul>
            )}

            {(styleChips.length > 0 || palette.length > 0) && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="video-analysis-style">
                    {styleChips.map((c) => (
                        <span key={c.label} className="px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/30 text-[10px] text-violet-200">
                            <span className="text-violet-400/70 uppercase tracking-wider mr-1">{c.label}</span>{c.value}
                        </span>
                    ))}
                    {palette.length > 0 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                            {palette.map((color, i) => (
                                <span key={i} className="w-3 h-3 rounded-full border border-white/20" style={{ backgroundColor: color }} title={color} />
                            ))}
                        </span>
                    )}
                </div>
            )}

            {analysis.segments?.length > 0 && (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowSegments(s => !s)}
                        className="text-[11px] text-violet-300 hover:text-violet-200 font-semibold"
                        data-testid="video-analysis-segments-toggle"
                    >
                        {showSegments ? '▾' : '▸'} {analysis.segments.length} scene{analysis.segments.length === 1 ? '' : 's'}
                    </button>
                    {showSegments && (
                        <ul className="mt-2 space-y-1.5 pl-3 border-l border-violet-500/20" data-testid="video-analysis-segments">
                            {analysis.segments.map((s, i) => (
                                <li key={i} className="text-[11px] text-zinc-400 leading-relaxed">
                                    <span className="text-violet-300 font-mono mr-2">[{formatDuration(s.start_seconds)}–{formatDuration(s.end_seconds)}]</span>
                                    {s.description}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {analysis.transcript?.text && (
                <div>
                    <button
                        type="button"
                        onClick={() => setShowTranscript(t => !t)}
                        className="text-[11px] text-violet-300 hover:text-violet-200 font-semibold"
                        data-testid="video-analysis-transcript-toggle"
                    >
                        {showTranscript ? '▾' : '▸'} Transcript ({analysis.transcript.language || 'detected'})
                    </button>
                    {showTranscript && (
                        <pre className="mt-2 p-2 bg-black/30 rounded-lg text-[11px] text-zinc-400 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto" data-testid="video-analysis-transcript">
                            {analysis.transcript.text}
                        </pre>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
                <div className="text-[10px] text-zinc-500">Preview ready — attach to your reference library or discard.</div>
                <button
                    type="button"
                    onClick={onAttach}
                    className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg whitespace-nowrap"
                    data-testid="attach-video-to-library-btn"
                >
                    Attach to library
                </button>
            </div>
        </div>
    );
};

// Reference Library shelf — shows every attached entry grouped by kind, with
// per-item remove buttons. Empty when library is empty.
const KIND_LABEL = { url: 'Web', 'social-post': 'Social posts', video: 'Videos' };
const KIND_ICON_BG = { url: 'bg-blue-500/20 border-blue-500/40', 'social-post': 'bg-pink-500/20 border-pink-500/40', video: 'bg-amber-500/20 border-amber-500/40' };

const ReferenceLibraryShelf = ({ libraryByKind, onRemove }) => {
    const totalItems = Object.values(libraryByKind).reduce((n, arr) => n + arr.length, 0);
    if (totalItems === 0) return null;
    return (
        <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl space-y-3" data-testid="reference-library-shelf">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-violet-300">Reference Library</div>
                    <div className="text-[10px] text-zinc-500">{totalItems} item{totalItems === 1 ? '' : 's'} attached · used by every concept regeneration</div>
                </div>
            </div>
            <div className="space-y-3">
                {['url', 'social-post', 'video'].map(kind => {
                    const items = libraryByKind[kind] || [];
                    if (items.length === 0) return null;
                    return (
                        <div key={kind} data-testid={`reference-library-group-${kind}`}>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                                {KIND_LABEL[kind]} · {items.length}
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                                {items.map(entry => (
                                    <div
                                        key={entry.id}
                                        className="relative flex items-center gap-2 px-2 py-2 bg-white/5 border border-white/10 rounded-lg flex-shrink-0 max-w-[260px]"
                                        data-testid={`reference-library-item-${entry.id}`}
                                    >
                                        {entry.thumbnail ? (
                                            <img
                                                src={entry.thumbnail}
                                                alt=""
                                                className={`w-10 h-10 rounded object-cover border ${KIND_ICON_BG[kind]}`}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <div className={`w-10 h-10 rounded border flex-shrink-0 ${KIND_ICON_BG[kind]}`} />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-white font-semibold truncate">{entry.title}</div>
                                            <div className="text-[10px] text-zinc-500 truncate">{entry.subtitle}</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onRemove(entry.id)}
                                            className="p-1 rounded text-zinc-500 hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                                            data-testid={`reference-library-remove-${entry.id}`}
                                            title="Remove from library"
                                        >
                                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};



export const ConceptGenerator = ({ onConceptReady, initialData = {} }) => {
    const [brandName, setBrandName] = useState(initialData.brandName || initialData.name || '');

    const [refMode, setRefMode] = useState('website-url');
    const [refWebsiteUrl, setRefWebsiteUrl] = useState('');
    const [refVideoUrl, setRefVideoUrl] = useState('');
    const [refVideoFile, setRefVideoFile] = useState(null);
    // Multi-image upload (Feb 2026). Each entry:
    // `{ id, name, data, mimeType, size }` where `data` is a base64 data URL.
    // The FIRST image still flows through the legacy `referenceImage` API
    // field for backward compat with vision-capable single-image models;
    // ALL images additionally go out via `extra_context.references[]`
    // (kind: 'image') so multi-image-aware backends can see the full set.
    const [refImageFiles, setRefImageFiles] = useState([]);
    // Document references (Feb 2026). PDF / DOCX / TXT / MD etc. attached as
    // context for concept generation. Sent through `extra_context.references[]`
    // (kind: 'document') with base64 data + mime so the backend can extract
    // text server-side if needed.
    const [refDocumentFiles, setRefDocumentFiles] = useState([]);
    // Free-text instructions the user wants the LLM to follow when
    // generating concepts (e.g. "keep it premium and minimalist", "include
    // a price callout in shot 3"). Embedded into the project idea AND sent
    // as an explicit `instructions` field so prompt engineers can give it
    // higher weight than the rest of the context.
    const [conceptInstructions, setConceptInstructions] = useState('');
    // Phase 1: real URL scrape via /ai/fetch-reference-async (1 credit per fetch).
    // null = nothing fetched yet; object = successful fetch attached.
    const [urlReference, setUrlReference] = useState(null);
    const [isFetchingUrl, setIsFetchingUrl] = useState(false);
    const [fetchProgress, setFetchProgress] = useState('');
    // Phase 1.5: social account fetch via /ai/fetch-social-account-async (10 credits).
    // socialInput = the string the user typed (URL or handle); socialPlatform =
    // either auto-detected from URL or chosen via the picker for bare handles.
    const [socialInput, setSocialInput] = useState('');
    const [socialPlatform, setSocialPlatform] = useState(null); // 'youtube' | 'tiktok' | 'instagram'
    const [socialResult, setSocialResult] = useState(null);
    const [isFetchingSocial, setIsFetchingSocial] = useState(false);
    const [socialFetchProgress, setSocialFetchProgress] = useState('');
    const [selectedSocialIds, setSelectedSocialIds] = useState(new Set());
    // Phase 2: video analysis via /ai/analyze-video-async (50 credits).
    // Used by both `video-url` and `video-upload` modes — the result shape is
    // identical regardless of source.
    const [videoAnalysis, setVideoAnalysis] = useState(null);
    const [isAnalyzingVideo, setIsAnalyzingVideo] = useState(false);
    const [videoAnalysisProgress, setVideoAnalysisProgress] = useState('');
    const [videoUploadPercent, setVideoUploadPercent] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    // ============ Reference Library (Phase 1 + 1.5 + 2 unified) ============
    // Single source of truth for everything attached to this concept session.
    // Each entry: { id, kind: 'url'|'social-post'|'video', title, subtitle,
    //               thumbnail, attachedAt, payload }
    // payload holds the original normalised data we send to the LLM.
    const [library, setLibrary] = useState([]);

    const addToLibrary = useCallback((entry) => {
        const id = entry.id || `${entry.kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setLibrary(prev => [...prev, { ...entry, id, attachedAt: new Date().toISOString() }]);
    }, []);

    const removeFromLibrary = useCallback((id) => {
        setLibrary(prev => prev.filter(e => e.id !== id));
    }, []);

    // Group library entries by kind for the shelf renderer.
    const libraryByKind = library.reduce((acc, e) => {
        (acc[e.kind] ||= []).push(e);
        return acc;
    }, {});
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const [industryId, setIndustryId] = useState(initialData.industryId || null);
    // Free-text industry label, used ONLY when industryId === 'other-industry'.
    // Capped at 40 chars on input. Swapped in for `industry.name` when
    // building the LLM brief and when sending the structured payload.
    const [customIndustryName, setCustomIndustryName] = useState(initialData.customIndustryName || '');
    const [videoTypeId, setVideoTypeId] = useState(initialData.videoTypeId || null);
    // Free-text name when user picks the "Other (Custom Video)" tile. Sent to
    // the backend in place of the canned videoType.name. Hard-capped at 30 chars.
    const [customVideoTypeName, setCustomVideoTypeName] = useState(initialData.customVideoTypeName || '');
    // Track whether the current industry/videoType were auto-suggested from a
    // reference video, so the UI can show "auto-picked, click to change".
    const [autoSuggestedFromVideo, setAutoSuggestedFromVideo] = useState(false);
    // Feb 2026 — Track whether the industry tile was auto-selected from the
    // saved brand (BrandScopePicker → App.handleBrandScopeApply →
    // initialData.industryAutoFromBrand). Used to render a friendly hint and
    // to prompt the user toward Video Type as the next action.
    const [autoSuggestedFromBrand, setAutoSuggestedFromBrand] = useState(
        Boolean(initialData.industryAutoFromBrand && initialData.industryId),
    );
    // Cast mode — 'with_actors' (default, concepts can use characters) or
    // 'product_only' (concepts must NOT use any people; focus on the product).
    // Persisted onto the project so Characters / Script / Storyboard skip
    // character generation in product-only mode.
    const [castMode, setCastMode] = useState('with_actors');

    const [concepts, setConcepts] = useState([]);
    const [selectedConceptIdx, setSelectedConceptIdx] = useState(null);
    const [error, setError] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const rawIndustry = CONCEPT_CATALOG.find(i => i.id === industryId);
    // When user picks "Other (custom industry)", swap in their typed label
    // so all downstream prompts / payloads reference the typed name.
    const industry = (rawIndustry && rawIndustry.id === 'other-industry' && customIndustryName.trim())
        ? { ...rawIndustry, name: customIndustryName.trim() }
        : rawIndustry;
    const rawVideoType = industry?.videoTypes.find(v => v.id === videoTypeId);
    // When user picks "Other (Custom Video)", swap in their typed label so all
    // downstream prompts / payloads reference the custom format name directly.
    const videoType = (rawVideoType && rawVideoType.id === 'other-custom' && customVideoTypeName.trim())
        ? { ...rawVideoType, name: customVideoTypeName.trim() }
        : rawVideoType;

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;
        if (refMode === 'image-upload') {
            handleImageFiles(files);
        } else if (refMode === 'document-upload') {
            handleDocumentFiles(files);
        } else {
            // Video upload — single file only.
            handleVideoFile(files[0]);
        }
    }, [refMode]);

    const handleVideoFile = (file) => {
        if (!file || !file.type.startsWith('video/')) {
            setError('Invalid file. Please upload a video.');
            return;
        }
        const MAX_MB = 100;
        if (file.size > MAX_MB * 1024 * 1024) {
            setError(`Video too large. Please upload a file smaller than ${MAX_MB}MB.`);
            return;
        }
        setError(null);
        setVideoAnalysis(null);
        // Keep the actual File object so we can PUT it to GCS directly.
        // `data` (data URL) kept around for potential preview use.
        const reader = new FileReader();
        reader.onloadend = () => {
            setRefVideoFile({
                name: file.name,
                data: reader.result,
                mimeType: file.type,
                size: file.size,
                file,
            });
        };
        reader.readAsDataURL(file);
    };

    const handleImageFile = (file) => {
        if (!file || !file.type.startsWith('image/')) {
            setError('Invalid file. Please upload an image (JPG, PNG, WEBP).');
            return;
        }
        const MAX_MB = 5;
        if (file.size > MAX_MB * 1024 * 1024) {
            setError(`Image too large. Please upload a file smaller than ${MAX_MB}MB.`);
            return;
        }
        setError(null);
        const reader = new FileReader();
        reader.onloadend = () => {
            const entry = {
                id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                name: file.name,
                data: reader.result,
                mimeType: file.type,
                size: file.size,
            };
            setRefImageFiles(prev => [...prev, entry]);
        };
        reader.readAsDataURL(file);
    };

    // Accept multiple files at once from the dropzone or file picker.
    const handleImageFiles = (fileList) => {
        if (!fileList) return;
        Array.from(fileList).forEach(handleImageFile);
    };

    // Document upload — PDF, DOCX, TXT, MD, etc. Sent as base64 data URL
    // via the `extra_context.references[]` array. Backend is expected to
    // decode the base64 and run server-side text extraction (the frontend
    // does NOT attempt to parse PDF/DOCX content here).
    const ACCEPTED_DOC_MIMES = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'text/markdown',
        'text/csv',
        'application/rtf',
    ];
    const ACCEPTED_DOC_EXTS = ['.pdf', '.doc', '.docx', '.txt', '.md', '.csv', '.rtf'];
    const isAcceptedDoc = (file) => {
        if (!file) return false;
        if (ACCEPTED_DOC_MIMES.includes(file.type)) return true;
        const name = (file.name || '').toLowerCase();
        return ACCEPTED_DOC_EXTS.some(ext => name.endsWith(ext));
    };

    const handleDocumentFile = (file) => {
        if (!isAcceptedDoc(file)) {
            setError('Unsupported document. Accepted: PDF, DOC, DOCX, TXT, MD, CSV, RTF.');
            return;
        }
        const MAX_MB = 10;
        if (file.size > MAX_MB * 1024 * 1024) {
            setError(`Document too large. Please upload a file smaller than ${MAX_MB}MB.`);
            return;
        }
        setError(null);
        const reader = new FileReader();
        reader.onloadend = () => {
            const entry = {
                id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                name: file.name,
                data: reader.result,        // base64 data URL
                mimeType: file.type || 'application/octet-stream',
                size: file.size,
            };
            setRefDocumentFiles(prev => [...prev, entry]);
        };
        reader.readAsDataURL(file);
    };

    const handleDocumentFiles = (fileList) => {
        if (!fileList) return;
        Array.from(fileList).forEach(handleDocumentFile);
    };

    const removeImageById = (id) => setRefImageFiles(prev => prev.filter(f => f.id !== id));
    const removeDocumentById = (id) => setRefDocumentFiles(prev => prev.filter(f => f.id !== id));

    // Fetch a URL via the admin-backend reference service. Costs 1 credit on
    // success. Result is stored in `urlReference` and rendered as a card.
    const handleFetchUrl = async () => {
        const url = refWebsiteUrl.trim();
        if (!url) {
            setError('Please enter a URL first.');
            return;
        }
        setError(null);
        setUrlReference(null);
        setIsFetchingUrl(true);
        setFetchProgress('Submitting URL…');
        try {
            const ref = await fetchUrlReference({
                url,
                projectId: initialData?.projectId,
                onProgress: (msg) => setFetchProgress(msg),
            });
            setUrlReference(ref);
        } catch (err) {
            console.error('URL fetch failed:', err);
            setError(err?.message || 'Failed to fetch URL. Please try again.');
        } finally {
            setIsFetchingUrl(false);
            setFetchProgress('');
        }
    };

    const handleClearUrlReference = () => {
        setUrlReference(null);
        setError(null);
    };

    const handleAttachUrlToLibrary = () => {
        if (!urlReference) return;
        addToLibrary({
            kind: 'url',
            title: urlReference.title || urlReference.url,
            subtitle: urlReference.siteName || 'Web',
            thumbnail: urlReference.images?.[0] || urlReference.favicon || null,
            payload: urlReference,
        });
        setUrlReference(null);
        setRefWebsiteUrl('');
    };

    // ============ Phase 1.5: Social account fetch ============
    // When the user types, auto-detect platform from URL; for bare handles
    // we keep socialPlatform set by the picker.
    const handleSocialInputChange = (val) => {
        setSocialInput(val);
        if (socialResult) setSocialResult(null);
        const detected = detectSocialPlatform(val);
        if (detected) setSocialPlatform(detected);
    };

    const isHandle = socialInput.trim() && !/^https?:\/\//i.test(socialInput.trim());
    const canFetchSocial = Boolean(socialInput.trim()) && (
        /^https?:\/\//i.test(socialInput.trim()) ? !!detectSocialPlatform(socialInput) : !!socialPlatform
    );

    const handleFetchSocial = async () => {
        const input = socialInput.trim();
        if (!input) {
            setError('Enter a URL or @handle first.');
            return;
        }
        setError(null);
        setSocialResult(null);
        setSelectedSocialIds(new Set());
        setIsFetchingSocial(true);
        setSocialFetchProgress('Submitting…');

        const isUrl = /^https?:\/\//i.test(input);
        const args = isUrl
            ? { url: input }
            : { platform: socialPlatform, handle: input };
        args.limit = 12;
        args.projectId = initialData?.projectId;
        args.onProgress = (msg) => setSocialFetchProgress(msg);

        try {
            const res = await fetchSocialAccount(args);
            setSocialResult(res);
        } catch (err) {
            console.error('Social fetch failed:', err);
            setError(err?.message || 'Failed to fetch social account. Please try again.');
        } finally {
            setIsFetchingSocial(false);
            setSocialFetchProgress('');
        }
    };

    const toggleSocialItem = (itemId) => {
        setSelectedSocialIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    };

    const handleClearSocial = () => {
        setSocialResult(null);
        setSelectedSocialIds(new Set());
        setError(null);
    };

    const handleAttachSocialToLibrary = () => {
        if (!socialResult || selectedSocialIds.size === 0) return;
        const selected = socialResult.items.filter(i => selectedSocialIds.has(i.id));
        selected.forEach(item => {
            addToLibrary({
                kind: 'social-post',
                title: item.title || item.text?.slice(0, 80) || `${socialResult.platform} post`,
                subtitle: `${socialResult.platform} · ${socialResult.profile.displayName || socialResult.handle}`,
                thumbnail: item.thumbnail || null,
                payload: { item, profile: socialResult.profile, platform: socialResult.platform, handle: socialResult.handle },
            });
        });
        // Keep the profile result on screen so the user can pick more, but clear the selection.
        setSelectedSocialIds(new Set());
    };

    // ============ Phase 2: Video Analysis (URL or upload) ============
    const handleAnalyzeVideoUrl = async () => {
        const url = refVideoUrl.trim();
        if (!url) {
            setError('Enter a video URL first.');
            return;
        }
        setError(null);
        setVideoAnalysis(null);
        setIsAnalyzingVideo(true);
        setVideoAnalysisProgress('Submitting…');
        try {
            const res = await analyzeVideo({
                videoUrl: url,
                projectId: initialData?.projectId,
                onProgress: (msg) => setVideoAnalysisProgress(msg),
            });
            setVideoAnalysis(res);
        } catch (err) {
            console.error('Video URL analysis failed:', err);
            setError(err?.message || 'Video analysis failed. Please try again.');
        } finally {
            setIsAnalyzingVideo(false);
            setVideoAnalysisProgress('');
        }
    };

    const handleAnalyzeVideoUpload = async () => {
        if (!refVideoFile?.file) {
            setError('Please pick a video file first.');
            return;
        }
        setError(null);
        setVideoAnalysis(null);
        setVideoUploadPercent(0);
        setIsAnalyzingVideo(true);
        setVideoAnalysisProgress('Requesting upload URL…');
        try {
            const f = refVideoFile.file;
            const { uploadUrl, gcsPath, requiredHeaders } = await getVideoUploadUrl({
                filename: f.name,
                contentType: f.type,
                sizeBytes: f.size,
                projectId: initialData?.projectId,
            });

            setVideoAnalysisProgress('Uploading video…');
            await uploadVideoToGcs({
                uploadUrl,
                requiredHeaders,
                file: f,
                onProgress: ({ percent }) => setVideoUploadPercent(percent),
            });

            setVideoAnalysisProgress('Upload complete. Starting analysis…');
            const res = await analyzeVideo({
                gcsPath,
                projectId: initialData?.projectId,
                onProgress: (msg) => setVideoAnalysisProgress(msg),
            });
            setVideoAnalysis(res);
        } catch (err) {
            console.error('Video upload analysis failed:', err);
            setError(err?.message || 'Video analysis failed. Please try again.');
        } finally {
            setIsAnalyzingVideo(false);
            setVideoAnalysisProgress('');
            setVideoUploadPercent(0);
        }
    };

    const handleClearVideoAnalysis = () => {
        setVideoAnalysis(null);
        setError(null);
    };

    const handleAttachVideoToLibrary = () => {
        if (!videoAnalysis) return;
        addToLibrary({
            kind: 'video',
            title: videoAnalysis.title || 'Untitled video',
            subtitle: `${videoAnalysis.siteName || videoAnalysis.sourceType || 'video'}${videoAnalysis.durationSeconds ? ` · ${formatDuration(videoAnalysis.durationSeconds)}` : ''}`,
            thumbnail: videoAnalysis.thumbnail || null,
            payload: videoAnalysis,
        });
        // Auto-suggest industry + video type from the analysis. We always
        // overwrite — the user explicitly attached this video as the brief
        // they want concepts to match. They can still override via the tiles.
        const { industry: suggestedIndustry, videoType: suggestedVideoType } = suggestFromVideoAnalysis(videoAnalysis);
        if (suggestedIndustry) {
            setIndustryId(suggestedIndustry.id);
            if (suggestedVideoType) setVideoTypeId(suggestedVideoType.id);
            setAutoSuggestedFromVideo(true);
        }
        setVideoAnalysis(null);
        setRefVideoUrl('');
        setRefVideoFile(null);
    };

    const formatDuration = (sec) => {
        if (!sec || sec <= 0) return '';
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const referenceSummary = () => {
        if (library.length === 0) return null;
        const counts = [];
        if (libraryByKind.url?.length) counts.push(`${libraryByKind.url.length} web`);
        if (libraryByKind['social-post']?.length) counts.push(`${libraryByKind['social-post'].length} social post${libraryByKind['social-post'].length === 1 ? '' : 's'}`);
        if (libraryByKind.video?.length) counts.push(`${libraryByKind.video.length} video${libraryByKind.video.length === 1 ? '' : 's'}`);
        return `Library: ${counts.join(' · ')}`;
    };

    // Track which concept is being viewed in the large detail modal.
    // null = modal closed; integer = index of concept currently expanded.
    const [expandedConceptIdx, setExpandedConceptIdx] = useState(null);

    // User can generate when they have brand + industry + videoType + at least
    // one reference. References live in the library, EXCEPT for product/brand
    // images and documents which still flow through dedicated API fields and
    // don't go into the visual library shelf.
    const hasImageReference = refImageFiles.length > 0;
    const hasDocumentReference = refDocumentFiles.length > 0;
    const referenceProvided = library.length > 0 || hasImageReference || hasDocumentReference;
    const customVideoTypeRequired = videoTypeId === 'other-custom';
    const customVideoTypeReady = !customVideoTypeRequired || customVideoTypeName.trim().length > 0;
    // When the user picks "Other" for industry, the typed label is required
    // before we let them generate concepts — otherwise the LLM gets a
    // useless `industry.name === "Other"`.
    const customIndustryRequired = industryId === 'other-industry';
    const customIndustryReady = !customIndustryRequired || customIndustryName.trim().length > 0;
    // Feb 2026: brand scope picked on Story step. When present, hide the
    // manual Brand Name + References + Instructions sections and treat those
    // gates as auto-satisfied (the saved-brand context replaces them).
    const hasBrandScope = Boolean(initialData.brandId);
    const canGenerate = (hasBrandScope || (Boolean(brandName.trim()) && referenceProvided)) && industry && videoType && customVideoTypeReady && customIndustryReady;

    const buildProjectIdea = () => {
        const brand = brandName.trim();
        const refSummaryParts = [];
        if (referenceSummary()) refSummaryParts.push(referenceSummary());
        if (hasImageReference) {
            refSummaryParts.push(`${refImageFiles.length} product / brand image${refImageFiles.length === 1 ? '' : 's'} uploaded`);
        }
        if (hasDocumentReference) {
            refSummaryParts.push(`${refDocumentFiles.length} document${refDocumentFiles.length === 1 ? '' : 's'} uploaded`);
        }
        // Feb 2026 — Saved-brand context. When the user picked a brand +
        // products on Story, unroll the full brand + product records into
        // the brief so the LLM writes concepts around ACTUAL product names
        // and descriptions instead of just the brand string. Without this
        // section every "brand ✓ picked" flow just says "Brand: gro" and
        // the LLM has nothing else to anchor on.
        const brandCtx = initialData.brandContext;
        if (brandCtx?.brand || (brandCtx?.products?.length || 0) > 0) {
            refSummaryParts.push(`saved brand: ${brandCtx?.brand?.name || brand}`);
        }
        const parts = [
            `Brand / Product: ${brand}`,
            `Industry: ${industry.name} — ${industry.desc}. (Marketing driver: ${industry.benefit})`,
            `Video Type: ${videoType.name} — ${videoType.desc}. (Goal: ${videoType.benefit})`,
            `Reference: ${refSummaryParts.length ? refSummaryParts.join(' · ') : 'none'}`,
        ];

        // Saved-brand rich context (Feb 2026). Injected BEFORE reference-library
        // items so the LLM anchors on real product data first, then reads any
        // user-attached web/social/video references as supplementary style
        // input.
        if (brandCtx?.brand || (brandCtx?.products?.length || 0) > 0) {
            parts.push('');
            parts.push(`--- SAVED BRAND: ${brandCtx?.brand?.name || brand} ---`);
            if (brandCtx?.brand?.description) {
                parts.push(`Brand description: ${brandCtx.brand.description}`);
            }
            if (brandCtx?.brand?.tagline) {
                parts.push(`Tagline: ${brandCtx.brand.tagline}`);
            }
            const tov = brandCtx?.brand?.profile?.communication?.tone_of_voice
                     || brandCtx?.brand?.profile?.tone_of_voice
                     || brandCtx?.brand?.profile?.tone;
            if (tov) parts.push(`Tone of voice: ${tov}`);
            const category = brandCtx?.brand?.profile?.category
                          || brandCtx?.brand?.profile?.industry;
            if (category) parts.push(`Category: ${category}`);
            const usp = brandCtx?.brand?.profile?.value_proposition
                     || brandCtx?.brand?.profile?.usp
                     || brandCtx?.brand?.profile?.differentiator;
            if (usp) parts.push(`Value proposition: ${usp}`);
            const audience = brandCtx?.brand?.profile?.target_audience
                          || brandCtx?.brand?.profile?.audience;
            if (audience) parts.push(`Target audience: ${audience}`);

            if ((brandCtx.products || []).length > 0) {
                parts.push('');
                parts.push(`Products in scope for this ad (${brandCtx.products.length}) — the concepts MUST feature these by name:`);
                brandCtx.products.forEach((p, i) => {
                    const line = [`  ${i + 1}. ${p.name}`];
                    if (p.price) line.push(`(${typeof p.price === 'object' ? (p.price.formatted || p.price.amount) : p.price})`);
                    parts.push(line.join(' '));
                    if (p.description) parts.push(`     · ${p.description}`);
                    if ((p.images || []).length > 0) {
                        parts.push(`     · ${p.images.length} product image${p.images.length === 1 ? '' : 's'} attached — see extra_context.references for vision-capable models.`);
                    }
                });
            }
            if ((brandCtx.services || []).length > 0) {
                parts.push('');
                parts.push(`Services in scope (${brandCtx.services.length}):`);
                brandCtx.services.forEach((s, i) => {
                    parts.push(`  ${i + 1}. ${s.name}${s.description ? ` — ${s.description}` : ''}`);
                });
            }
            parts.push('--- END SAVED BRAND ---');
        }

        // Iterate over every library entry and fold its content into the brief.
        library.forEach((entry, idx) => {
            parts.push('');
            if (entry.kind === 'url') {
                const ref = entry.payload;
                parts.push(`--- REFERENCE ${idx + 1}: WEB · ${ref.siteName || ref.url} ---`);
                if (ref.title) parts.push(`Page title: ${ref.title}`);
                if (ref.highlights?.length) {
                    parts.push('Key points:');
                    ref.highlights.slice(0, 5).forEach(h => parts.push(`  • ${h}`));
                }
                if (ref.text) {
                    parts.push('Excerpt:');
                    parts.push(ref.text.slice(0, 1200).trim());
                }
                parts.push('--- END ---');
            } else if (entry.kind === 'social-post') {
                const { item, profile, platform, handle } = entry.payload;
                parts.push(`--- REFERENCE ${idx + 1}: ${platform.toUpperCase()} POST · ${profile.displayName || handle} ---`);
                const headline = item.title || (item.text ? item.text.slice(0, 80) : 'post');
                parts.push(`Headline: ${headline}`);
                if (item.text && item.text !== headline) parts.push(`Caption: ${item.text.slice(0, 400)}`);
                if (item.highlights?.length) parts.push(`Tags: #${item.highlights.slice(0, 6).join(' #')}`);
                if (item.engagement) {
                    const eng = [];
                    if (item.engagement.views) eng.push(`${item.engagement.views.toLocaleString()} views`);
                    if (item.engagement.likes) eng.push(`${item.engagement.likes.toLocaleString()} likes`);
                    if (item.engagement.comments) eng.push(`${item.engagement.comments.toLocaleString()} comments`);
                    if (eng.length) parts.push(`Engagement: ${eng.join(' · ')}`);
                }
                parts.push('--- END ---');
            } else if (entry.kind === 'video') {
                const v = entry.payload;
                parts.push(`--- REFERENCE ${idx + 1}: VIDEO · ${v.title || 'untitled'} ---`);
                if (v.durationSeconds) parts.push(`Duration: ${formatDuration(v.durationSeconds)} (${v.durationSeconds}s)`);
                if (v.text) parts.push(`Summary: ${v.text}`);
                if (v.highlights?.length) {
                    parts.push('Highlights:');
                    v.highlights.slice(0, 6).forEach(h => parts.push(`  • ${h}`));
                }
                const vs = v.visualStyle || {};
                if (vs.mood || vs.pacing) {
                    parts.push('Visual style:');
                    if (vs.mood) parts.push(`  - Mood: ${vs.mood}`);
                    if (vs.pacing) parts.push(`  - Pacing: ${vs.pacing}`);
                    if (vs.color_palette?.length) parts.push(`  - Palette: ${vs.color_palette.join(', ')}`);
                    if (vs.camera_movement) parts.push(`  - Camera: ${vs.camera_movement}`);
                    if (vs.lighting) parts.push(`  - Lighting: ${vs.lighting}`);
                }
                const aud = v.audioStyle || {};
                if (aud.music_genre || aud.voice_tone) {
                    parts.push('Audio style:');
                    if (aud.music_genre) parts.push(`  - Music: ${aud.music_genre}`);
                    if (aud.voice_tone) parts.push(`  - Voice: ${aud.voice_tone}`);
                    if (aud.has_dialogue !== undefined) parts.push(`  - Dialogue: ${aud.has_dialogue ? 'yes' : 'no'} · Narration: ${aud.has_narration ? 'yes' : 'no'}`);
                }
                if (v.segments?.length) {
                    parts.push(`Scene breakdown (${v.segments.length} segments):`);
                    v.segments.slice(0, 6).forEach((s, i) => {
                        parts.push(`  ${i + 1}. [${formatDuration(s.start_seconds)}–${formatDuration(s.end_seconds)}] ${s.description}`);
                    });
                }
                parts.push('--- END ---');
            }
        });

        if (initialData.duration) parts.push(`Target duration: ${initialData.duration}`);
        if (castMode === 'product_only') {
            parts.push('Cast Mode: PRODUCT ONLY — the final video must feature zero people on screen. No actors, no hands holding the product, no faces, no crowds in background. The product itself is the hero of every shot. Off-screen voiceover / narration IS allowed (and encouraged where useful).');
        } else {
            parts.push('Cast Mode: WITH ACTORS — on-screen humans are welcome.');
        }

        // Surface uploaded image filenames so non-vision LLMs at least know
        // they exist and how many are attached. Vision-capable models get
        // the raw bytes via `referenceImage` / `extra_context.references[]`.
        if (hasImageReference) {
            parts.push('');
            parts.push(`--- UPLOADED PRODUCT / BRAND IMAGES (${refImageFiles.length}) ---`);
            refImageFiles.forEach((img, i) => parts.push(`  ${i + 1}. ${img.name}`));
            parts.push('--- END ---');
        }

        // Document references — list names + mime so the LLM acknowledges
        // them in the brief. Backend is responsible for extracting/inlining
        // the actual document text when calling vision-capable models.
        if (hasDocumentReference) {
            parts.push('');
            parts.push(`--- UPLOADED DOCUMENTS (${refDocumentFiles.length}) ---`);
            refDocumentFiles.forEach((doc, i) => parts.push(`  ${i + 1}. ${doc.name} (${doc.mimeType || 'document'})`));
            parts.push('--- END ---');
        }

        // User-provided concept instructions — these must be honoured during
        // generation. Embed AT THE END of the brief so the LLM treats them
        // as the most recent / authoritative directive on top of everything
        // above.
        const instructions = (conceptInstructions || '').trim();
        if (instructions) {
            parts.push('');
            parts.push('--- USER INSTRUCTIONS (FOLLOW STRICTLY) ---');
            parts.push(instructions);
            parts.push('--- END INSTRUCTIONS ---');
        }

        parts.push('');

        // Reference-video MIRROR mode: when the user attached a video reference,
        // they did so explicitly to create a SIMILAR video. Switch the brief
        // from "generate strategically different concepts" to "generate
        // concepts that closely match the reference video's format, mood,
        // pacing, structure, and style."
        const videoRefs = libraryByKind.video || [];
        if (videoRefs.length > 0) {
            const refTitles = videoRefs.map(v => `"${v.title}"`).join(', ');
            if (castMode === 'product_only') {
                parts.push(`Generate 4 product-only video concepts for "${brand}" that CLOSELY MATCH the reference video${videoRefs.length > 1 ? 's' : ''} above (${refTitles}). The user attached the reference specifically because they want concepts in the same style and format. Each concept must:`);
            } else {
                parts.push(`Generate 4 video concepts for "${brand}" that CLOSELY MATCH the reference video${videoRefs.length > 1 ? 's' : ''} above (${refTitles}). The user attached the reference specifically because they want concepts in the same style and format. Each concept must:`);
            }
            parts.push(`  • Adopt the reference's overall mood, pacing, camera language, and lighting.`);
            parts.push(`  • Mirror its scene structure (opening hook style, segment count and rhythm, ending beat) — adapt the content for "${brand}" but keep the structural DNA.`);
            parts.push(`  • Match its audio style (music genre, voice tone, dialogue/narration choice, SFX density).`);
            parts.push(`  • Reuse the reference's colour palette as inspiration for visual styling.`);
            parts.push(`  • Concepts can vary slightly in narrative angle (emotional, problem-solution, demo, proof, etc.) but the ${videoType.name} format and reference style must remain dominant.`);
            if (castMode === 'product_only') {
                parts.push(`  • NO characters, actors, hands, or faces on screen. Voiceover narration is allowed if the reference uses it.`);
            }
            parts.push(`  • Each concept must name "${brand}" explicitly in its title and description.`);
            parts.push(`  • Every hook must land in the first 2 seconds.`);
            parts.push(`Tailor everything for a ${industry.name.toLowerCase()} audience.`);
        } else if (castMode === 'product_only') {
            parts.push(`Generate 4 distinct, strategically different product-only video concepts for "${brand}". NO concept may include characters, actors, hands, or faces on screen. Off-screen voiceover narration is permitted and often useful. Concepts must rely primarily on the product, packaging, ingredients, environment, typography, colour, texture, motion, lighting, and sound design — with narration supporting where it adds value. Each concept must name "${brand}" explicitly in its title and description. Every hook must land in the first 2 seconds. Stay faithful to the ${videoType.name} format for a ${industry.name.toLowerCase()} audience.`);
        } else {
            parts.push(`Generate 4 distinct, strategically different video concepts. Each concept must name "${brand}" explicitly in its title and description. Each concept must use a different angle (emotional, pattern-interrupt, problem-solution, proof-driven). Every hook must land in the first 2 seconds. Stay faithful to the ${videoType.name} format for a ${industry.name.toLowerCase()} audience.`);
        }
        return parts.join('\n');
    };

    const handleGenerateConcepts = async () => {
        if (!canGenerate) {
            setError('Please fill brand name, reference, industry, and video type.');
            return;
        }
        setError(null);
        setSelectedConceptIdx(null);
        setIsGenerating(true);
        try {
            // Resolve the user's AI Toolkit model pick for the `generate_concepts`
            // capability. If the user hasn't picked one, send nothing and let the
            // admin backend fall back to its capability default (Gemini 3 Pro).
            const modelKey = getModelForCapability('generate_concepts') || undefined;
            const apiArgs = {
                projectIdea: buildProjectIdea(),
                count: 4,
                genre: industry.name,
                language: 'English',
                modelKey,
            };

            // Send user instructions as an explicit top-level field so prompt
            // engineers on the backend can give them stronger weight than the
            // rest of the brief.
            const instructionsTrimmed = (conceptInstructions || '').trim();
            if (instructionsTrimmed) {
                apiArgs.instructions = instructionsTrimmed;
            }

            // First uploaded image flows through the legacy `referenceImage`
            // single-image field (vision-capable models like Gemini 3 Pro can
            // study it directly). ALL uploaded images additionally go out via
            // `extra_context.references[]` below so multi-image-aware backends
            // see the full set.
            if (refImageFiles.length > 0) {
                const primary = refImageFiles[0];
                apiArgs.referenceImage = {
                    name: primary.name,
                    mime_type: primary.mimeType,
                    data: primary.data,
                };
            }
            // Build a single `extra_context.references[]` array from the
            // library, uploaded images, and uploaded documents so the admin
            // backend gets full structured creative input for every attached
            // reference.
            const extraRefs = [];
            for (const entry of library) {
                if (entry.kind === 'url') {
                    const r = entry.payload;
                    extraRefs.push({
                        kind: 'url',
                        url: r.url,
                        title: r.title,
                        site_name: r.siteName,
                        text: r.text,
                        highlights: r.highlights,
                        images: r.images,
                    });
                } else if (entry.kind === 'social-post') {
                    const { item, profile, platform, handle } = entry.payload;
                    extraRefs.push({
                        kind: 'social-post',
                        url: item.postUrl,
                        title: item.title || (item.text ? item.text.slice(0, 80) : ''),
                        site_name: platform,
                        handle,
                        profile_display_name: profile.displayName,
                        profile_followers: profile.followers,
                        text: item.text,
                        highlights: item.highlights,
                        images: item.images,
                        engagement: item.engagement,
                        media_type: item.mediaType,
                        duration_seconds: item.durationSeconds,
                    });
                } else if (entry.kind === 'video') {
                    const v = entry.payload;
                    extraRefs.push({
                        kind: 'video',
                        url: v.sourceUrl,
                        title: v.title,
                        site_name: v.siteName || v.sourceType,
                        text: v.text,
                        highlights: v.highlights,
                        images: v.images,
                        media_type: 'video',
                        duration_seconds: v.durationSeconds,
                        segments: v.segments,
                        transcript: v.transcript,
                        visual_style: v.visualStyle,
                        audio_style: v.audioStyle,
                    });
                }
            }
            // Uploaded product/brand images — included in extra_context so
            // multi-image-aware backends can see them all (not just the
            // primary one in `referenceImage`).
            refImageFiles.forEach((img, i) => {
                extraRefs.push({
                    kind: 'image',
                    title: img.name,
                    name: img.name,
                    mime_type: img.mimeType,
                    data: img.data,             // base64 data URL
                    size_bytes: img.size,
                    is_primary: i === 0,
                });
            });
            // Uploaded documents (PDF, DOCX, TXT, MD, CSV, RTF). Backend is
            // expected to decode `data` (base64 data URL) and run server-side
            // text extraction before feeding into the LLM context.
            refDocumentFiles.forEach((doc) => {
                extraRefs.push({
                    kind: 'document',
                    title: doc.name,
                    name: doc.name,
                    mime_type: doc.mimeType,
                    data: doc.data,             // base64 data URL
                    size_bytes: doc.size,
                });
            });
            // Feb 2026 — Saved-brand product images. When the user picked a
            // brand + products on Story, we push each product's images into
            // extra_context.references[] so vision-capable LLMs (Gemini 3 Pro,
            // GPT-4o) can actually see the product and write concepts that
            // reflect its real look. Without this the LLM only had the
            // brand name as anchor. Falls back safely on non-vision models
            // (they ignore unknown reference kinds).
            const brandCtxImages = initialData.brandContext;
            if (brandCtxImages?.products?.length) {
                brandCtxImages.products.forEach((p) => {
                    (p.images || []).forEach((imgUrl, i) => {
                        if (!imgUrl) return;
                        extraRefs.push({
                            kind: 'product-image',
                            title: `${p.name}${p.images.length > 1 ? ` (${i + 1}/${p.images.length})` : ''}`,
                            name: p.name,
                            product_id: p.id,
                            product_name: p.name,
                            product_description: p.description || '',
                            url: imgUrl,
                            is_primary: i === 0,
                        });
                    });
                });
            }
            if (extraRefs.length > 0) {
                apiArgs.extraContext = { references: extraRefs };
            }
            // Brand scope (Feb 2026): forward saved-brand id + selected
            // product/service ids so the admin loads scoped brand context
            // and the LLM is grounded in the exact products the user picked.
            if (initialData.brandId)                                                   apiArgs.brandId    = initialData.brandId;
            if (Array.isArray(initialData.productIds) && initialData.productIds.length) apiArgs.productIds = initialData.productIds;
            if (Array.isArray(initialData.serviceIds) && initialData.serviceIds.length) apiArgs.serviceIds = initialData.serviceIds;
            const res = await generateConceptsApi(apiArgs);
            const parsed = Array.isArray(res?.concepts) ? res.concepts : [];
            if (parsed.length === 0) {
                console.warn('Admin returned 0 concepts, falling back to templates');
                setConcepts(buildConcepts({ brandName, industry, videoType, duration: initialData.duration, castMode }));
                setError('AI returned no concepts — showing template suggestions instead. Click Regenerate to retry the AI.');
                return;
            }
            setConcepts(parsed);
        } catch (err) {
            console.error('Concept generation failed:', err);
            setConcepts(buildConcepts({ brandName, industry, videoType, duration: initialData.duration, castMode }));
            setError(`AI call failed (${err?.message || 'unknown error'}). Showing template suggestions. Click Regenerate to retry.`);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSelectConcept = () => {
        if (selectedConceptIdx === null || !concepts[selectedConceptIdx]) return;
        const c = concepts[selectedConceptIdx];
        const duration = initialData.duration || '';
        const lines = [
            `Brand / Product: ${brandName.trim()}`,
            `Industry: ${industry.name} — ${industry.desc}.`,
            `Video Type: ${videoType.name} — ${videoType.desc}. Goal: ${videoType.benefit}.`,
            `Reference: ${referenceSummary()}`,
            `Cast Mode: ${castMode === 'product_only' ? 'PRODUCT ONLY — no actors, no hands, no faces on screen. Off-screen voiceover / narration is allowed. The product is the visual hero.' : 'WITH ACTORS — on-screen humans are welcome.'}`,
        ];
        if (duration) lines.push(`Target duration: ${duration}.`);
        lines.push('');
        lines.push(`CONCEPT — "${c.title}"`);
        if (c.hook) lines.push(`Hook (first 2 seconds): ${c.hook}`);
        lines.push('');
        lines.push(c.description);
        lines.push('');
        if (castMode === 'product_only') {
            lines.push(`The final script must be PRODUCT ONLY on screen — write it with zero on-screen characters and no dialogue between people. Off-screen voiceover / narration IS allowed and should be used where it strengthens the story. Use the product, packaging, motion, close-ups, typography, sound design, and optional narration as the storytellers. Mention "${brandName.trim()}" prominently across shots. Deliver on "${videoType.benefit.toLowerCase()}" for the ${industry.name.toLowerCase()} audience.`);
        } else {
            lines.push(`The final script should mention "${brandName.trim()}" prominently, match the tone of a ${videoType.name.toLowerCase()} for the ${industry.name.toLowerCase()} audience, and deliver on "${videoType.benefit.toLowerCase()}".`);
        }
        onConceptReady(lines.join('\n'), { brandName: brandName.trim(), castMode });
    };

    return (
        <div className="space-y-6" data-testid="concept-generator">
            {/* Feb 2026: When the user has picked a saved brand on Story,
                show a compact confirmation instead of the manual brand name /
                references / instructions blocks. The saved brand carries
                all of that context server-side. */}
            {hasBrandScope && (
                <section
                    className="flex items-center gap-3 rounded-2xl border border-violet-500/40 bg-violet-500/[0.06] px-4 py-3"
                    data-testid="brand-scope-confirmation"
                >
                    <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-violet-300">✓</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white">
                            Using saved brand{initialData.brandName ? `: ${initialData.brandName}` : ''}
                        </div>
                        <div className="text-[11px] text-zinc-400">
                            {Array.isArray(initialData.productIds) ? initialData.productIds.length : 0} product
                            {Array.isArray(initialData.productIds) && initialData.productIds.length === 1 ? '' : 's'} scoped
                            {Array.isArray(initialData.serviceIds) && initialData.serviceIds.length > 0
                                ? ` · ${initialData.serviceIds.length} service${initialData.serviceIds.length === 1 ? '' : 's'}`
                                : ''}
                            . Change or clear from the Story step.
                        </div>
                        {autoSuggestedFromBrand && industry && (
                            <div
                                className="mt-1.5 text-[11px] text-emerald-300 flex items-center gap-1.5"
                                data-testid="brand-scope-industry-hint"
                            >
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Industry auto-selected: <span className="font-semibold">{industry.icon} {industry.name}</span> — now pick a video type below.
                            </div>
                        )}
                    </div>
                </section>
            )}
            {!hasBrandScope && (<>
            <section>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
                    1 · Brand / Product / Business name
                </div>
                <input
                    type="text"
                    value={brandName}
                    onChange={e => setBrandName(e.target.value)}
                    placeholder="e.g., Bella Skincare, Acme Cloud, Ramen House"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20"
                    data-testid="brand-name-input"
                />
            </section>

            <section>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
                    2 · Reference (website, social, or video)
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                    {[
                        { id: 'website-url', label: 'Website / Social URL' },
                        { id: 'social-account', label: 'Fetch Social Account' },
                        { id: 'video-url', label: 'Reference Video URL' },
                        { id: 'video-upload', label: 'Upload Reference Video' },
                        { id: 'image-upload', label: 'Upload Product / Brand Images' },
                        { id: 'document-upload', label: 'Upload Documents' },
                    ].map(opt => (
                        <button
                            key={opt.id}
                            type="button"
                            onClick={() => { setRefMode(opt.id); setError(null); if (opt.id !== 'website-url') setUrlReference(null); if (opt.id !== 'social-account') { setSocialResult(null); setSelectedSocialIds(new Set()); } if (opt.id !== 'video-url' && opt.id !== 'video-upload') setVideoAnalysis(null); }}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                                refMode === opt.id
                                    ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                                    : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/25'
                            }`}
                            data-testid={`ref-mode-${opt.id}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {refMode === 'website-url' && (
                    <div className="space-y-3">
                        <div className="flex gap-2">
                            <input
                                type="url"
                                value={refWebsiteUrl}
                                onChange={e => { setRefWebsiteUrl(e.target.value); if (urlReference) setUrlReference(null); }}
                                placeholder="https://your-brand.com or https://instagram.com/your-handle"
                                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20"
                                data-testid="ref-website-url-input"
                                disabled={isFetchingUrl}
                            />
                            <button
                                type="button"
                                onClick={handleFetchUrl}
                                disabled={!refWebsiteUrl.trim() || isFetchingUrl || !!urlReference}
                                className="px-4 py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap"
                                data-testid="fetch-url-reference-btn"
                                title="Costs 1 credit"
                            >
                                {isFetchingUrl ? <LoadingSpinner className="w-4 h-4" /> : null}
                                {isFetchingUrl ? 'Fetching…' : urlReference ? 'Fetched ✓' : 'Fetch · 1 credit'}
                            </button>
                        </div>

                        {isFetchingUrl && fetchProgress && (
                            <div className="p-3 bg-violet-500/5 border border-violet-500/20 rounded-xl text-xs text-violet-300" data-testid="ref-fetch-progress">
                                {fetchProgress}
                            </div>
                        )}

                        {urlReference && (
                            <div className="p-4 bg-white/5 border border-violet-500/30 rounded-xl space-y-3" data-testid="url-reference-card">
                                <div className="flex items-start gap-3">
                                    {urlReference.favicon ? (
                                        <img
                                            src={urlReference.favicon}
                                            alt=""
                                            className="w-5 h-5 rounded flex-shrink-0 mt-0.5"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div className="w-5 h-5 rounded bg-violet-500/20 flex-shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{urlReference.siteName}</div>
                                        <div className="text-sm font-semibold text-white leading-tight">{urlReference.title || urlReference.url}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleClearUrlReference}
                                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 flex-shrink-0"
                                        data-testid="ref-url-clear-btn"
                                        title="Discard preview"
                                    >
                                        <DeleteIcon className="w-4 h-4" />
                                    </button>
                                </div>

                                {urlReference.highlights?.length > 0 && (
                                    <ul className="space-y-1 pl-2" data-testid="url-reference-highlights">
                                        {urlReference.highlights.slice(0, 3).map((h, i) => (
                                            <li key={i} className="text-xs text-zinc-400 leading-relaxed flex gap-2">
                                                <span className="text-violet-400 flex-shrink-0">•</span>
                                                <span>{h}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {urlReference.images?.length > 0 && (
                                    <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="url-reference-images">
                                        {urlReference.images.slice(0, 4).map((src, i) => (
                                            <img
                                                key={i}
                                                src={src}
                                                alt=""
                                                className="w-16 h-16 object-cover rounded-lg flex-shrink-0 border border-white/10"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ))}
                                    </div>
                                )}

                                <div className="flex items-center justify-between gap-2 pt-1">
                                    <div className="text-[10px] text-zinc-500">Preview ready — attach to your reference library or discard.</div>
                                    <button
                                        type="button"
                                        onClick={handleAttachUrlToLibrary}
                                        className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg whitespace-nowrap"
                                        data-testid="attach-url-to-library-btn"
                                    >
                                        Attach to library
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {refMode === 'social-account' && (
                    <div className="space-y-3" data-testid="social-account-panel">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={socialInput}
                                onChange={e => handleSocialInputChange(e.target.value)}
                                placeholder="@MKBHD, glossier, or https://tiktok.com/@charlidamelio"
                                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20"
                                data-testid="social-account-input"
                                disabled={isFetchingSocial}
                            />
                            <button
                                type="button"
                                onClick={handleFetchSocial}
                                disabled={!canFetchSocial || isFetchingSocial || !!socialResult}
                                className="px-4 py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap"
                                data-testid="fetch-social-account-btn"
                                title="Costs 10 credits"
                            >
                                {isFetchingSocial ? <LoadingSpinner className="w-4 h-4" /> : null}
                                {isFetchingSocial ? 'Fetching…' : socialResult ? 'Fetched ✓' : 'Fetch · 10 credits'}
                            </button>
                        </div>

                        {/* Platform picker — only shown for bare handles (no URL) */}
                        {isHandle && !socialResult && (
                            <div className="flex flex-wrap gap-2">
                                <span className="text-[10px] uppercase tracking-wider text-zinc-500 self-center mr-1">Platform:</span>
                                {[
                                    { id: 'youtube', label: 'YouTube' },
                                    { id: 'tiktok', label: 'TikTok' },
                                    { id: 'instagram', label: 'Instagram' },
                                ].map(p => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setSocialPlatform(p.id)}
                                        className={`px-3 py-1 rounded-lg border text-[11px] font-semibold transition-all ${
                                            socialPlatform === p.id
                                                ? 'bg-violet-500/15 border-violet-500/40 text-violet-300'
                                                : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/25'
                                        }`}
                                        data-testid={`social-platform-${p.id}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isFetchingSocial && socialFetchProgress && (
                            <div className="p-3 bg-violet-500/5 border border-violet-500/20 rounded-xl text-xs text-violet-300" data-testid="social-fetch-progress">
                                {socialFetchProgress}
                                {socialPlatform && socialPlatform !== 'youtube' && (
                                    <div className="text-[10px] text-zinc-500 mt-1">
                                        Tip: TikTok / Instagram fetches use Apify and can take ~60-90 seconds.
                                    </div>
                                )}
                            </div>
                        )}

                        {socialResult && (
                            <div className="p-4 bg-white/5 border border-violet-500/30 rounded-xl space-y-4" data-testid="social-account-result">
                                {/* Profile header */}
                                <div className="flex items-start gap-3 pb-3 border-b border-white/10">
                                    {socialResult.profile.avatar ? (
                                        <img
                                            src={socialResult.profile.avatar}
                                            alt=""
                                            className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-white/10"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-violet-500/20 flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-semibold text-white truncate">{socialResult.profile.displayName || socialResult.handle}</div>
                                            {socialResult.profile.verified && (
                                                <span className="text-violet-400 text-xs" title="Verified">✓</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                                            {socialResult.platform}
                                            {socialResult.profile.followers ? ` · ${socialResult.profile.followers.toLocaleString()} followers` : ''}
                                            {` · ${socialResult.itemsCount} posts fetched`}
                                        </div>
                                        {socialResult.profile.bio && (
                                            <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{socialResult.profile.bio}</div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleClearSocial}
                                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 flex-shrink-0"
                                        data-testid="social-account-clear-btn"
                                        title="Discard"
                                    >
                                        <DeleteIcon className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Post grid */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                                            Pick the posts to attach as references
                                        </div>
                                        <div className="text-[10px] text-violet-300 font-semibold">
                                            {selectedSocialIds.size} selected
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="social-post-grid">
                                        {socialResult.items.map((item) => {
                                            const selected = selectedSocialIds.has(item.id);
                                            const headline = item.title || item.text?.slice(0, 60) || '(no caption)';
                                            return (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    onClick={() => toggleSocialItem(item.id)}
                                                    className={`relative text-left rounded-lg border overflow-hidden transition-all ${
                                                        selected
                                                            ? 'border-violet-500 ring-2 ring-violet-500/40'
                                                            : 'border-white/10 hover:border-white/30'
                                                    }`}
                                                    data-testid={`social-post-card-${item.id}`}
                                                >
                                                    {item.thumbnail ? (
                                                        <div className="aspect-[4/5] w-full bg-black overflow-hidden">
                                                            <img
                                                                src={item.thumbnail}
                                                                alt=""
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="aspect-[4/5] w-full bg-zinc-900 flex items-center justify-center">
                                                            <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{item.mediaType}</span>
                                                        </div>
                                                    )}
                                                    {selected && (
                                                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center">
                                                            ✓
                                                        </div>
                                                    )}
                                                    {item.durationSeconds && (
                                                        <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/80 text-white text-[9px] font-semibold">
                                                            {Math.floor(item.durationSeconds / 60)}:{String(item.durationSeconds % 60).padStart(2, '0')}
                                                        </div>
                                                    )}
                                                    <div className="p-2 bg-zinc-950">
                                                        <div className="text-[10px] text-zinc-300 leading-tight line-clamp-2">{headline}</div>
                                                        {(item.engagement?.views || item.engagement?.likes) && (
                                                            <div className="text-[9px] text-zinc-500 mt-1">
                                                                {item.engagement.views ? `${formatCount(item.engagement.views)} views` : ''}
                                                                {item.engagement.views && item.engagement.likes ? ' · ' : ''}
                                                                {item.engagement.likes ? `${formatCount(item.engagement.likes)} likes` : ''}
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {selectedSocialIds.size > 0 && (
                                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
                                        <div className="text-[10px] text-zinc-500">
                                            {selectedSocialIds.size} post{selectedSocialIds.size === 1 ? '' : 's'} selected — attach to your library to use them.
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleAttachSocialToLibrary}
                                            className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg whitespace-nowrap"
                                            data-testid="attach-social-to-library-btn"
                                        >
                                            Attach {selectedSocialIds.size} to library
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {refMode === 'video-url' && (
                    <div className="space-y-3">
                        <div className="flex gap-2">
                            <input
                                type="url"
                                value={refVideoUrl}
                                onChange={e => { setRefVideoUrl(e.target.value); if (videoAnalysis) setVideoAnalysis(null); }}
                                placeholder="https://youtu.be/... or direct MP4 URL"
                                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20"
                                data-testid="ref-video-url-input"
                                disabled={isAnalyzingVideo}
                            />
                            <button
                                type="button"
                                onClick={handleAnalyzeVideoUrl}
                                disabled={!refVideoUrl.trim() || isAnalyzingVideo || !!videoAnalysis}
                                className="px-4 py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 whitespace-nowrap"
                                data-testid="analyze-video-url-btn"
                                title="Costs 50 credits"
                            >
                                {isAnalyzingVideo ? <LoadingSpinner className="w-4 h-4" /> : null}
                                {isAnalyzingVideo ? 'Analysing…' : videoAnalysis ? 'Analysed ✓' : 'Analyse · 50 credits'}
                            </button>
                        </div>
                    </div>
                )}
                {refMode === 'video-upload' && (
                    refVideoFile ? (
                        <div className="space-y-3">
                            <div className="p-3 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between gap-2">
                                <p className="flex-1 text-sm text-zinc-300 truncate">{refVideoFile.name} <span className="text-zinc-500 text-xs">· {(refVideoFile.size / (1024 * 1024)).toFixed(1)} MB</span></p>
                                <button
                                    type="button"
                                    onClick={handleAnalyzeVideoUpload}
                                    disabled={isAnalyzingVideo || !!videoAnalysis}
                                    className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-all flex items-center gap-2 whitespace-nowrap"
                                    data-testid="analyze-video-upload-btn"
                                    title="Costs 50 credits"
                                >
                                    {isAnalyzingVideo ? <LoadingSpinner className="w-3 h-3" /> : null}
                                    {isAnalyzingVideo ? 'Analysing…' : videoAnalysis ? 'Analysed ✓' : 'Analyse · 50 credits'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setRefVideoFile(null); setVideoAnalysis(null); }}
                                    disabled={isAnalyzingVideo}
                                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid="ref-video-clear-btn"
                                >
                                    <DeleteIcon className="w-4 h-4" />
                                </button>
                            </div>
                            {isAnalyzingVideo && videoUploadPercent > 0 && videoUploadPercent < 100 && (
                                <div className="space-y-1" data-testid="video-upload-progress">
                                    <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                                        <div className="h-full bg-violet-500 transition-all" style={{ width: `${videoUploadPercent}%` }} />
                                    </div>
                                    <div className="text-[10px] text-zinc-500">{videoUploadPercent}% uploaded</div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <label
                            htmlFor="concept-video-upload"
                            onDragEnter={e => { e.preventDefault(); setIsDragging(true); }}
                            onDragOver={e => { e.preventDefault(); }}
                            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                            onDrop={handleDrop}
                            className={`flex flex-col items-center justify-center w-full p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                                isDragging ? 'border-violet-500 bg-violet-500/5' : 'border-white/10 hover:border-white/25 bg-white/5'
                            }`}
                            data-testid="ref-video-upload-dropzone"
                        >
                            <UploadIcon className="w-6 h-6 text-zinc-500 mb-2" />
                            <div className="text-xs text-zinc-400">Drop a video or click to browse (max 100MB)</div>
                            <input
                                id="concept-video-upload"
                                ref={fileInputRef}
                                type="file"
                                accept="video/*"
                                onChange={e => { const f = e.target.files?.[0]; if (f) handleVideoFile(f); }}
                                className="hidden"
                            />
                        </label>
                    )
                )}
                {/* Shared progress + analysis result card for both video modes */}
                {(refMode === 'video-url' || refMode === 'video-upload') && isAnalyzingVideo && videoAnalysisProgress && (
                    <div className="mt-3 p-3 bg-violet-500/5 border border-violet-500/20 rounded-xl text-xs text-violet-300" data-testid="video-analysis-progress">
                        {videoAnalysisProgress}
                        <div className="text-[10px] text-zinc-500 mt-1">
                            Tip: Gemini watches the full video — analysis can take 30-90 seconds for longer clips.
                        </div>
                    </div>
                )}
                {(refMode === 'video-url' || refMode === 'video-upload') && videoAnalysis && (
                    <VideoAnalysisCard
                        analysis={videoAnalysis}
                        onClear={handleClearVideoAnalysis}
                        onAttach={handleAttachVideoToLibrary}
                        formatDuration={formatDuration}
                    />
                )}
                {refMode === 'image-upload' && (
                    <div className="space-y-3" data-testid="image-upload-section">
                        {refImageFiles.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="image-thumbnails-grid">
                                {refImageFiles.map((img, i) => (
                                    <div key={img.id} className="relative group p-2 bg-white/5 border border-white/10 rounded-xl">
                                        <img
                                            src={img.data}
                                            alt={img.name}
                                            className="w-full aspect-square object-cover rounded-lg mb-1.5"
                                        />
                                        <p className="text-[10px] text-zinc-400 truncate" title={img.name}>{img.name}</p>
                                        {i === 0 && (
                                            <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-violet-500/80 text-white text-[9px] font-bold rounded">PRIMARY</span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => removeImageById(img.id)}
                                            className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-red-400 hover:bg-red-500/80 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                            data-testid={`ref-image-remove-${i}`}
                                            aria-label={`Remove ${img.name}`}
                                        >
                                            <DeleteIcon className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <label
                            htmlFor="concept-image-upload"
                            onDragEnter={e => { e.preventDefault(); setIsDragging(true); }}
                            onDragOver={e => { e.preventDefault(); }}
                            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                            onDrop={handleDrop}
                            className={`flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                                isDragging ? 'border-violet-500 bg-violet-500/5' : 'border-white/10 hover:border-white/25 bg-white/5'
                            }`}
                            data-testid="ref-image-upload-dropzone"
                        >
                            <UploadIcon className="w-6 h-6 text-zinc-500 mb-2" />
                            <div className="text-xs text-zinc-400">
                                {refImageFiles.length > 0 ? 'Add more images' : 'Drop product / brand images or click to browse'}
                                <span className="block text-[10px] text-zinc-500 mt-0.5">Multiple files supported · 5MB each · JPG, PNG, WEBP</span>
                            </div>
                            <input
                                id="concept-image-upload"
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={e => { handleImageFiles(e.target.files); e.target.value = ''; }}
                                className="hidden"
                                data-testid="ref-image-upload-input"
                            />
                        </label>
                    </div>
                )}
                {refMode === 'document-upload' && (
                    <div className="space-y-3" data-testid="document-upload-section">
                        {refDocumentFiles.length > 0 && (
                            <div className="space-y-2" data-testid="document-list">
                                {refDocumentFiles.map((doc, i) => (
                                    <div key={doc.id} className="p-3 bg-white/5 border border-white/10 rounded-xl flex items-center gap-3">
                                        <div className="w-10 h-10 flex items-center justify-center bg-violet-500/10 border border-violet-500/30 rounded-lg flex-shrink-0">
                                            <span className="text-[10px] font-bold text-violet-300 uppercase">
                                                {(doc.name.split('.').pop() || 'doc').slice(0, 4)}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-zinc-200 truncate" title={doc.name}>{doc.name}</p>
                                            <p className="text-[10px] text-zinc-500">{(doc.size / 1024).toFixed(1)} KB · {doc.mimeType || 'document'}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeDocumentById(doc.id)}
                                            className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10"
                                            data-testid={`ref-doc-remove-${i}`}
                                            aria-label={`Remove ${doc.name}`}
                                        >
                                            <DeleteIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <label
                            htmlFor="concept-doc-upload"
                            onDragEnter={e => { e.preventDefault(); setIsDragging(true); }}
                            onDragOver={e => { e.preventDefault(); }}
                            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
                            onDrop={handleDrop}
                            className={`flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                                isDragging ? 'border-violet-500 bg-violet-500/5' : 'border-white/10 hover:border-white/25 bg-white/5'
                            }`}
                            data-testid="ref-doc-upload-dropzone"
                        >
                            <UploadIcon className="w-6 h-6 text-zinc-500 mb-2" />
                            <div className="text-xs text-zinc-400">
                                {refDocumentFiles.length > 0 ? 'Add more documents' : 'Drop documents or click to browse'}
                                <span className="block text-[10px] text-zinc-500 mt-0.5">Multiple files supported · 10MB each · PDF, DOC, DOCX, TXT, MD, CSV, RTF</span>
                            </div>
                            <input
                                id="concept-doc-upload"
                                type="file"
                                accept={[...ACCEPTED_DOC_MIMES, ...ACCEPTED_DOC_EXTS].join(',')}
                                multiple
                                onChange={e => { handleDocumentFiles(e.target.files); e.target.value = ''; }}
                                className="hidden"
                                data-testid="ref-doc-upload-input"
                            />
                        </label>
                    </div>
                )}
                <p className="mt-2 text-[10px] text-zinc-500">
                    {refMode === 'website-url'
                        ? 'Tip: click "Fetch" to scrape the URL. The page title, key points, and images will be fed to the AI for richer, on-brand concepts. (1 credit per fetch.)'
                        : refMode === 'social-account'
                        ? 'Tip: paste a YouTube / TikTok / Instagram URL or @handle. We fetch up to 12 recent posts; pick the ones to attach. (10 credits per fetch.)'
                        : refMode === 'video-url' || refMode === 'video-upload'
                        ? 'Tip: click "Analyse" to have Gemini watch the full video — you get a transcript, scene breakdown, and visual/audio style notes the AI uses to craft on-brand concepts. (50 credits per analysis.)'
                        : refMode === 'document-upload'
                        ? 'Tip: upload PDFs, briefs, brand guidelines, or transcripts. The first attached file gets richer treatment; all files are sent to the AI as additional creative context.'
                        : 'Tip: upload multiple product / brand images for richer context. The first image is treated as the primary reference for vision-capable models; the rest are sent as supporting context.'}
                </p>

                <ReferenceLibraryShelf libraryByKind={libraryByKind} onRemove={removeFromLibrary} />

                {/* Free-text instructions the user wants the LLM to follow.
                    Shown ALWAYS (regardless of refMode) so users can guide
                    the concept generation in plain language — e.g. "keep
                    it premium minimalist", "feature a price callout in
                    shot 3", "match the tone of our reference doc". */}
                <div className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl" data-testid="concept-instructions-section">
                    <label htmlFor="concept-instructions-input" className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">
                        Instructions for AI <span className="text-zinc-600 font-normal normal-case tracking-normal">(optional)</span>
                    </label>
                    <textarea
                        id="concept-instructions-input"
                        value={conceptInstructions}
                        onChange={(e) => setConceptInstructions(e.target.value.slice(0, 1000))}
                        placeholder="e.g. Keep it premium and minimalist. Include a price callout in shot 3. Match the tone of our brand guidelines document."
                        rows={3}
                        className="w-full p-3 bg-black/30 border border-white/10 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/40 resize-none"
                        data-testid="concept-instructions-textarea"
                    />
                    <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-zinc-500">These instructions are passed verbatim to the AI and treated as priority directives.</span>
                        <span className="text-[10px] text-zinc-500" data-testid="concept-instructions-counter">{conceptInstructions.length} / 1000</span>
                    </div>
                </div>
            </section>
            </>)}

            <section>
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                        3 · Industry
                    </div>
                    {autoSuggestedFromVideo && industryId && (
                        <div className="text-[10px] text-violet-300 flex items-center gap-1.5" data-testid="industry-auto-hint">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                            Auto-picked from your reference video — click any tile to change.
                        </div>
                    )}
                    {autoSuggestedFromBrand && industryId && !autoSuggestedFromVideo && (
                        <div className="text-[10px] text-emerald-300 flex items-center gap-1.5" data-testid="industry-auto-from-brand-hint">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            Auto-selected from your brand — click any tile to change, or pick a video type below.
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {CONCEPT_CATALOG.map(ind => (
                        <TooltipCard
                            key={ind.id}
                            title={`${ind.icon} ${ind.name}`}
                            subtitle={ind.desc}
                            benefit={ind.benefit}
                            selected={industryId === ind.id}
                            onClick={() => {
                                setIndustryId(ind.id);
                                setVideoTypeId(null);
                                setCustomVideoTypeName('');
                                // Keep the custom-industry text only when the
                                // user lands on "Other"; clear it otherwise.
                                if (ind.id !== 'other-industry') setCustomIndustryName('');
                                setConcepts([]);
                                setAutoSuggestedFromVideo(false);
                                setAutoSuggestedFromBrand(false);
                            }}
                            testid={`industry-card-${ind.id}`}
                        />
                    ))}
                </div>
                {industryId === 'other-industry' && (
                    <div className="mt-3" data-testid="custom-industry-wrap">
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                            Describe your industry
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={customIndustryName}
                                onChange={(e) => setCustomIndustryName(e.target.value.slice(0, 40))}
                                maxLength={40}
                                placeholder="e.g. Renewable energy, Pet care, Logistics"
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pr-14 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all"
                                data-testid="custom-industry-input"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 tabular-nums" data-testid="custom-industry-counter">
                                {customIndustryName.length}/40
                            </span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1.5">
                            This label is sent to the AI as the industry context (max 40 characters).
                        </p>
                    </div>
                )}
            </section>

            {industry && (
                <section>
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                            4 · Video type · <span className="text-zinc-500 normal-case tracking-normal">for {industry.name}</span>
                        </div>
                        {autoSuggestedFromVideo && videoTypeId && (
                            <div className="text-[10px] text-violet-300 flex items-center gap-1.5" data-testid="video-type-auto-hint">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                                Auto-picked — click any tile to change.
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {industry.videoTypes.map(vt => (
                            <TooltipCard
                                key={vt.id}
                                title={vt.name}
                                subtitle={vt.desc}
                                benefit={vt.benefit}
                                selected={videoTypeId === vt.id}
                                onClick={() => { setVideoTypeId(vt.id); if (vt.id !== 'other-custom') setCustomVideoTypeName(''); setConcepts([]); setAutoSuggestedFromVideo(false); }}
                                testid={`videotype-card-${vt.id}`}
                            />
                        ))}
                    </div>
                    {videoTypeId === 'other-custom' && (
                        <div className="mt-3" data-testid="custom-video-type-wrap">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                                Describe your custom video type
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={customVideoTypeName}
                                    onChange={(e) => { setCustomVideoTypeName(e.target.value.slice(0, 30)); setConcepts([]); }}
                                    maxLength={30}
                                    placeholder="e.g. Behind-the-scenes reel"
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pr-14 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all"
                                    data-testid="custom-video-type-input"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 tabular-nums" data-testid="custom-video-type-counter">
                                    {customVideoTypeName.length}/30
                                </span>
                            </div>
                            <p className="text-[11px] text-zinc-500 mt-1.5">
                                This label will be sent to the AI as the video format (max 30 characters).
                            </p>
                        </div>
                    )}
                </section>
            )}

            {industry && videoType && (
                <section>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
                        5 · Cast
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: 'with_actors', label: 'With actor/s', desc: 'People can appear on screen. Default.' },
                            { id: 'product_only', label: 'Product only', desc: 'Zero people on screen. Off-screen narration allowed.' },
                        ].map(opt => {
                            const active = castMode === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => { setCastMode(opt.id); setConcepts([]); }}
                                    className={`group relative text-left px-4 py-3 rounded-xl border transition-all flex-1 min-w-[180px] ${
                                        active
                                            ? 'bg-violet-500/15 border-violet-500/60 ring-1 ring-violet-500/30'
                                            : 'bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/10'
                                    }`}
                                    data-testid={`cast-mode-${opt.id}`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className={`w-3 h-3 rounded-full border-2 ${active ? 'border-violet-400 bg-violet-400' : 'border-zinc-500'}`} />
                                        <div className="font-semibold text-sm text-white">{opt.label}</div>
                                    </div>
                                    <div className="text-[11px] text-zinc-400 leading-snug">{opt.desc}</div>
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-3 rounded-xl text-xs">
                    {error}
                </div>
            )}

            {concepts.length === 0 ? (
                <button
                    type="button"
                    onClick={handleGenerateConcepts}
                    disabled={!canGenerate || isGenerating}
                    className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full transition-all text-sm"
                    data-testid="generate-concepts-btn"
                >
                    {isGenerating ? <LoadingSpinner className="w-4 h-4" /> : <MagicWandIcon className="w-4 h-4" />}
                    {isGenerating ? 'Generating concepts…' : 'Generate Concepts'}
                </button>
            ) : (
                <>
                    <section data-testid="concept-results">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
                            6 · Pick your concept · <span className="text-zinc-500 normal-case tracking-normal">click any card to select, or expand for a full read</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {concepts.map((c, i) => {
                                const selected = selectedConceptIdx === i;
                                return (
                                    <div
                                        key={i}
                                        className={`relative rounded-xl border transition-all ${
                                            selected
                                                ? 'bg-violet-500/15 border-violet-500/60 ring-1 ring-violet-500/30'
                                                : 'bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/10'
                                        }`}
                                        data-testid={`concept-card-${i}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setSelectedConceptIdx(i)}
                                            className="w-full text-left p-5"
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-3">
                                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">Concept {i + 1}</div>
                                                {selected && <div className="text-[10px] font-bold text-violet-400">✓ Selected</div>}
                                            </div>
                                            <div className="font-semibold text-base text-white leading-snug mb-3">{c.title}</div>
                                            {c.hook && (
                                                <div className="text-sm text-violet-300 mb-3 italic leading-relaxed">
                                                    <span className="text-[10px] uppercase tracking-wider text-violet-400/70 font-bold not-italic mr-1">Hook</span> {c.hook}
                                                </div>
                                            )}
                                            <div className="text-sm text-zinc-300 leading-relaxed line-clamp-5">{c.description}</div>
                                        </button>
                                        <div className="px-5 pb-3 -mt-1 flex items-center justify-end">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setExpandedConceptIdx(i); }}
                                                className="text-[11px] font-semibold text-violet-300 hover:text-violet-200 px-2 py-1 rounded hover:bg-violet-500/10 transition-all flex items-center gap-1"
                                                data-testid={`concept-expand-btn-${i}`}
                                                title="Open in large view"
                                            >
                                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                                Expand
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={handleGenerateConcepts}
                            disabled={isGenerating}
                            className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/25 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-full transition-all text-sm"
                            data-testid="regenerate-concepts-btn"
                        >
                            {isGenerating ? <LoadingSpinner className="w-4 h-4" /> : <MagicWandIcon className="w-4 h-4" />}
                            {isGenerating ? 'Regenerating…' : 'Regenerate'}
                        </button>
                        <button
                            type="button"
                            onClick={handleSelectConcept}
                            disabled={selectedConceptIdx === null}
                            className="flex-[2] flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full transition-all text-sm"
                            data-testid="use-selected-concept-btn"
                        >
                            Use this concept →
                        </button>
                    </div>
                </>
            )}

            {/* Large readable concept detail modal */}
            {expandedConceptIdx !== null && concepts[expandedConceptIdx] && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-sm"
                    onClick={() => setExpandedConceptIdx(null)}
                    data-testid="concept-detail-modal-backdrop"
                >
                    <div
                        className="relative w-full max-w-3xl max-h-[90vh] bg-[#0f1115] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="concept-detail-modal"
                    >
                        {/* Header */}
                        <div className="px-8 py-6 border-b border-white/10 flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-300 mb-2">
                                    Concept {expandedConceptIdx + 1} of {concepts.length}
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight">
                                    {concepts[expandedConceptIdx].title}
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => setExpandedConceptIdx(null)}
                                className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 flex-shrink-0"
                                data-testid="concept-detail-close-btn"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Body — scrollable */}
                        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
                            {concepts[expandedConceptIdx].hook && (
                                <div>
                                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-400 mb-2">Hook</div>
                                    <p className="text-base sm:text-lg text-violet-200 italic leading-relaxed" data-testid="concept-detail-hook">
                                        {concepts[expandedConceptIdx].hook}
                                    </p>
                                </div>
                            )}

                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">Description</div>
                                <p className="text-base text-zinc-200 leading-relaxed whitespace-pre-line" data-testid="concept-detail-description">
                                    {concepts[expandedConceptIdx].description}
                                </p>
                            </div>

                            {/* Render any additional structured fields the LLM may include. */}
                            {Object.entries(concepts[expandedConceptIdx]).map(([key, value]) => {
                                if (['title', 'hook', 'description'].includes(key)) return null;
                                if (value == null || value === '') return null;
                                const renderable = typeof value === 'string'
                                    ? value
                                    : Array.isArray(value)
                                        ? value.filter(Boolean).map(v => typeof v === 'string' ? v : JSON.stringify(v)).join('\n• ')
                                        : null;
                                if (!renderable) return null;
                                return (
                                    <div key={key}>
                                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">
                                            {key.replace(/_/g, ' ')}
                                        </div>
                                        <p className="text-base text-zinc-300 leading-relaxed whitespace-pre-line">
                                            {Array.isArray(value) ? '• ' + renderable : renderable}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-4 border-t border-white/10 flex items-center justify-between gap-3 bg-black/30">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setExpandedConceptIdx(Math.max(0, expandedConceptIdx - 1))}
                                    disabled={expandedConceptIdx === 0}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid="concept-detail-prev-btn"
                                >
                                    ← Prev
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setExpandedConceptIdx(Math.min(concepts.length - 1, expandedConceptIdx + 1))}
                                    disabled={expandedConceptIdx === concepts.length - 1}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid="concept-detail-next-btn"
                                >
                                    Next →
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setSelectedConceptIdx(expandedConceptIdx); setExpandedConceptIdx(null); }}
                                className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold rounded-full"
                                data-testid="concept-detail-select-btn"
                            >
                                Use this concept →
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
